import { inject, Injectable, signal } from '@angular/core';
import { HostApiService } from 'lib-host-interface';
import { ProfilesService } from './profile.service';
import { AppHostUserInfo } from '../model/user.model';
import { ModUserProfile } from 'projects/app-nmls-entity-profile/src/app/models/profiles.response';
import { catchError, firstValueFrom, of, tap, timeout } from 'rxjs';

export interface SessionInfo {
  expiresInMilliSeconds?: string;
  expirationTime?: string;
  sessionExists?: string;
  username?: string;
}

export interface GlobalContextProps {
  classification: string;
  permissions: string[];
  userInfo: AppHostUserInfo;
  session: SessionInfo;
  modProfile: ModUserProfile;
}

@Injectable()
export class GlobalContext {
  hostApi = inject(HostApiService);

  private readonly HOST_TIMEOUT_MS = 5000;

  store = signal<GlobalContextProps>({
    classification: 'Company',
    permissions: [],
    userInfo: {},
    session: {},
    modProfile: {
      primeUser: '',
      firstName: '',
      lastName: '',
      primaryEmail: '',
      associations: [],
    },
  });

  modProfileService = inject(ProfilesService);

  getModProfile() {
    return firstValueFrom(
      this.modProfileService.getUserProfile().pipe(
        tap((modProfile) => {
          this.store.update((state) => ({ ...state, modProfile }));
        }),
      ),
    );
  }

  getSession() {
    return firstValueFrom(
      this.modProfileService.checkSession().pipe(
        tap((session) => {
          this.store.update((state) => ({ ...state, session }));
        }),
      ),
    );
  }

  loadDeferredData() {
    this.getModProfile().catch((e) => console.warn('[GlobalContext] getModProfile failed:', e));
    this.getSession().catch((e) => console.warn('[GlobalContext] getSession failed:', e));
  }

  getClassification() {
    return firstValueFrom(
      this.hostApi?.entitlements.classification.pipe(
        timeout(this.HOST_TIMEOUT_MS),
        tap((classification) => {
          this.store.update((state) => ({ ...state, classification }));
        }),
        catchError(() => of(this.store().classification)),
      ),
    );
  }

  getPermissions() {
    return firstValueFrom(
      this.hostApi?.entitlements.permissions.pipe(
        timeout(this.HOST_TIMEOUT_MS),
        tap((permissions) => {
          this.store.update((state) => ({ ...state, permissions }));
        }),
        catchError(() => of(this.store().permissions)),
      ),
    );
  }

  getUserInfo() {
    return firstValueFrom(
      this.hostApi?.user.info.pipe(
        timeout(this.HOST_TIMEOUT_MS),
        tap((userInfo) => {
          this.store.update((state) => ({ ...state, userInfo }));
        }),
        catchError(() => of(this.store().userInfo)),
      ),
    );
  }
}
